import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ApiChallenge } from "griptonite-api-client";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

import {
  useChallengeQuery,
  useCreateChallengeMutation,
  useUpdateChallengeMutation,
} from "@/hooks/challenges";
import { useCurrentVenueQuery } from "@/hooks/venues";
import { handleFormError } from "@/utils/handleFormError";

import FormErrorAlert from "../FormErrorAlert";
import CheckboxInput from "../forms/CheckboxInput";
import { MediaSelectInput } from "../forms/MediaInput";
import TextInput, { DateInput, TextareaInput } from "../forms/TextInput";
import VenueSelect from "../forms/VenueSelect";
import BottomButtons from "./BottomButtons";
import {
  ChallengeDetailsFormSchema,
  ChallengeDetailsFormValues,
  challengeDetailsFormValuesToPayload,
  challengeToDetailsFormValues,
} from "./forms";
import PublishWarningsAlert from "./PublishWarningsAlert";

const ChallengeDetailsForm = ({
  challengeUuid,
  challengeType,
  startDate,
}: {
  challengeUuid?: string;
  challengeType?: "CHECKIN";
  startDate?: string;
}) => {
  const isCreating = !challengeUuid;

  const { data: venue } = useCurrentVenueQuery();

  const { data: challenge, isPending } = useChallengeQuery(challengeUuid, venue?.uuid);

  const defaultValues = useMemo(
    () => challengeToDetailsFormValues(challenge, venue, startDate, challengeType),
    [challenge, venue, startDate, challengeType],
  );

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(ChallengeDetailsFormSchema),
    defaultValues,
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const createMutation = useCreateChallengeMutation();
  const updateMutation = useUpdateChallengeMutation(challenge?.uuid, venue?.uuid);
  const navigate = useNavigate();

  if (!isCreating && isPending) {
    return null;
  }

  const onSubmit = async (values: ChallengeDetailsFormValues) => {
    const payload = challengeDetailsFormValuesToPayload(values);
    if (isCreating) {
      const newChallenge = await createMutation.mutateAsync(
        { ...payload, isHidden: true },
        {
          onError: async (error) => {
            handleFormError(error, methods);
          },
        },
      );
      navigate({
        to: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
        from: "/gym/$venueUuid/challenges/create",
        params: {
          challengeUuid: newChallenge.uuid,
        },
        replace: true,
      });
    } else {
      const challenge = await updateMutation.mutateAsync(payload, {
        onError: async (error) => {
          handleFormError(error, methods);
        },
      });
      if (challenge.isHidden) {
        // go to next tab
        navigate({
          to: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
          from: "/gym/$venueUuid/challenges/$challengeUuid/profile/details",
          params: {
            challengeUuid: challenge.uuid,
          },
          replace: true,
        });
      }
    }
  };

  return (
    <Stack
      sx={{ padding: 1.5, gap: 1, flex: 1, overflowY: "auto", minHeight: 0 }}
      component="form"
      autoComplete="off"
      onSubmit={methods.handleSubmit(onSubmit as (vals: typeof defaultValues) => Promise<void>)}
    >
      <FormProvider {...methods}>
        <PublishWarningsAlert challenge={challenge} tab="details" />
        <FormErrorAlert />
        <Box sx={{ flex: 1 }}>
          <FormFields challenge={challenge} />
        </Box>
        <BottomButtons
          challenge={challenge}
          formState={methods.formState}
          nextLink={
            challenge
              ? {
                  to: "/gym/$venueUuid/challenges/$challengeUuid/profile/rules",
                  params: { challengeUuid: challenge.uuid },
                }
              : undefined
          }
        />
      </FormProvider>
    </Stack>
  );
};

const FormFields = ({ challenge }: { challenge?: ApiChallenge }) => {
  const { watch } = useFormContext();
  const startTime = watch("startTime");
  const isEndless = watch("isEndless");

  return (
    <Stack spacing={2}>
      <VenueSelect
        name="venues"
        label="Gyms"
        multiple
        placeholder="Select gyms for this challenge"
      />
      <TextInput name="name" label="Challenge name" />
      <Box>
        {/* in a box as there's a weird sibling style rule on helper text for checkboxes */}
        <CheckboxInput name="isEndless" label="Endless challenge" />
      </Box>
      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <DateInput
            name="startTime"
            label="Starts at"
            {...(challenge === undefined ? { minDate: "TODAY" } : {})}
            placeholder=""
            disabled={!!challenge?.entriesCount}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          {!isEndless ? (
            <DateInput
              name="endTime"
              label="Ends at"
              minDate={startTime && format(startTime, "yyyy-MM-dd")}
              defaultTime="23:59"
              placeholder=""
            />
          ) : null}
        </Box>
      </Stack>
      <TextareaInput name="description" label="Description" />
      <MediaSelectInput name="media" label="Challenge media" accept="image/*,video/*" multiple />
      <Box>
        <Typography fontSize={15} marginBottom={1} fontWeight="bold">
          Links
        </Typography>
        <Stack gap={1} sx={{ label: { fontSize: 13 } }}>
          <TextInput name="websiteUrl" label="Website" />
          <TextInput name="facebookUrl" label="Facebook" />
          <TextInput name="instagramUrl" label="Instagram" />
        </Stack>
      </Box>
    </Stack>
  );
};

export default ChallengeDetailsForm;
